import { FC } from "react";
import Container from "react-bootstrap/Container";
import ButtonGroup from "react-bootstrap/esm/ButtonGroup";
import { FaEdit, FaFile, FaPlus, FaTrash } from "react-icons/fa";
import Alert from "react-bootstrap/esm/Alert";
import Card from "react-bootstrap/esm/Card";
import Table from "react-bootstrap/Table";
import { useRouteParams } from "app/route/route";
import { NavigationScreen } from "app/component/screen/NavigationScreen";
import { RouteBreadcrumbs } from "app/route/RouteBreadcrumbs";
import {
    createReportVersionAccessInformationCredentialsRoute,
    createReportVersionAccessInformationFileRoute,
    deleteReportVersionAccessInformationCredentialsRoute,
    deleteReportVersionAccessInformationFileRoute,
    editReportVersionAccessInformationCredentialsRoute,
    editReportVersionAccessInformationFileRoute,
    editReportVersionAccessInformationRoute,
    reportVersionAccessInformationRoute,
} from "app/route/Routes";
import {
    ReportAccessInformationCredentialsFieldsFragment,
    ReportAccessInformationFileFieldsFragment,
    useReportVersionAccessInformationCredentialsQuery,
    useReportVersionAccessInformationFilesQuery,
    useReportVersionAccessInformationQuery,
} from "app/api/graph/types";
import { reportVersionGraphId } from "app/api/graph/helpers";
import { LoadingScreen } from "app/component/screen/LoadingScreen";
import { ErrorScreen } from "app/component/screen/ErrorScreen";
import { HtmlContent } from "app/component/util/HtmlContent";
import { Toolbar, ToolbarButtons, ToolbarTitle } from "app/component/util/Toolbar";
import { ButtonLink } from "app/component/button/ButtonLink";
import { useReportAccessInformationPermissions } from "app/api/graph/hook/use-report-access-information-permissions";
import { mapNodes } from "app/util/graph";
import { useGeneralPermissions } from "app/api/graph/hook/use-general-permissions";
import { RevealablePassword } from "app/component/util/RevealablePassword";
import { LoadingSpinner } from "app/component/util/LoadingSpinner";

type CredentialsCardProps = {
    reportId: number;
    versionId: number;
    credentials: ReportAccessInformationCredentialsFieldsFragment;
};

const CredentialsCard = ({ reportId, versionId, credentials }: CredentialsCardProps) => {
    const permissions = useGeneralPermissions(credentials.permissions);

    return (
        <Card key={credentials.id} className="mb-3">
            <Card.Header>
                <Toolbar>
                    <ToolbarTitle>Credentials</ToolbarTitle>
                    <ToolbarButtons>
                        {permissions.canUpdate ? (
                            <ButtonLink
                                variant="warning"
                                to={editReportVersionAccessInformationCredentialsRoute.build({
                                    reportId,
                                    versionId,
                                    credentialsId: credentials._id,
                                })}
                            >
                                <FaEdit /> Edit
                            </ButtonLink>
                        ) : null}
                        {permissions.canDelete ? (
                            <ButtonLink
                                variant="danger"
                                to={deleteReportVersionAccessInformationCredentialsRoute.build({
                                    reportId,
                                    versionId,
                                    credentialsId: credentials._id,
                                })}
                            >
                                <FaTrash /> Delete
                            </ButtonLink>
                        ) : null}
                    </ToolbarButtons>
                </Toolbar>
            </Card.Header>
            <Card.Body>
                <Table>
                    <tbody>
                        <tr>
                            <th>Username</th>
                            <td>{credentials.username ?? "-"}</td>
                        </tr>
                        <tr>
                            <th>Password</th>
                            <td>
                                {credentials.password != null ? (
                                    <RevealablePassword password={credentials.password} />
                                ) : (
                                    "-"
                                )}
                            </td>
                        </tr>
                        <tr>
                            <th>URL</th>
                            <td>{credentials.url ?? "-"}</td>
                        </tr>
                        <tr>
                            <th>Notes</th>
                            <td>{credentials.notes ? <HtmlContent html={credentials.notes} /> : "-"}</td>
                        </tr>
                    </tbody>
                </Table>
            </Card.Body>
        </Card>
    );
};

type FileCardProps = {
    reportId: number;
    versionId: number;
    file: ReportAccessInformationFileFieldsFragment;
};

const FileCard = ({ reportId, versionId, file }: FileCardProps) => {
    const permissions = useGeneralPermissions(file.permissions);

    return (
        <Card key={file.id} className="mb-3">
            <Card.Header>
                <Toolbar>
                    <ToolbarTitle>{file.reportMediaObject.originalName}</ToolbarTitle>
                    <ToolbarButtons>
                        {permissions.canUpdate ? (
                            <ButtonLink
                                variant="warning"
                                to={editReportVersionAccessInformationFileRoute.build({
                                    reportId,
                                    versionId,
                                    fileId: file._id,
                                })}
                            >
                                <FaEdit /> Edit
                            </ButtonLink>
                        ) : null}
                        {permissions.canDelete ? (
                            <ButtonLink
                                variant="danger"
                                to={deleteReportVersionAccessInformationFileRoute.build({
                                    reportId,
                                    versionId,
                                    fileId: file._id,
                                })}
                            >
                                <FaTrash /> Delete
                            </ButtonLink>
                        ) : null}
                    </ToolbarButtons>
                </Toolbar>
            </Card.Header>
            <Card.Body>
                <figure className="figure">
                    <a href={file.reportMediaObject.contentUrl ?? ""} target="_blank" rel="noopener noreferrer">
                        {file.reportMediaObject.mimeType?.startsWith("image/") ? (
                            <img
                                className="figure-img img-fluid"
                                src={file.reportMediaObject.contentUrl ?? ""}
                                alt={file.reportMediaObject.originalName ?? "Screenshot"}
                            />
                        ) : (
                            <>
                                <FaFile />
                                {file.reportMediaObject.originalName}
                            </>
                        )}
                    </a>
                </figure>

                {file.description != null && <HtmlContent html={file.description} />}
            </Card.Body>
        </Card>
    );
};

type CredentialsSectionProps = {
    reportId: number;
    versionId: number;
};

export const CredentialsSection: FC<CredentialsSectionProps> = ({ reportId, versionId }) => {
    const { data, loading, error } = useReportVersionAccessInformationCredentialsQuery({
        notifyOnNetworkStatusChange: true,
        variables: { versionId: reportVersionGraphId(versionId) },
    });
    const accessInformation = data?.reportVersion?.accessInformation;
    const { canCreateCredentials } = useReportAccessInformationPermissions(accessInformation?.permissions);

    if (loading && !accessInformation) {
        return <LoadingSpinner />;
    }
    if (error) {
        return <Alert variant="danger">{error.message}</Alert>;
    }
    if (!accessInformation) {
        return <Alert variant="danger">Not found.</Alert>;
    }

    return (
        <>
            <Toolbar>
                <ToolbarTitle>
                    <h2>Credentials</h2>
                </ToolbarTitle>
                <ToolbarButtons>
                    {canCreateCredentials ? (
                        <ButtonLink
                            to={createReportVersionAccessInformationCredentialsRoute.build({
                                reportId,
                                versionId,
                            })}
                            variant="success"
                        >
                            <FaPlus /> New credentials
                        </ButtonLink>
                    ) : null}
                </ToolbarButtons>
            </Toolbar>

            {accessInformation.credentials?.edges?.length === 0 && (
                <Alert variant="warning">No credentials have been provided.</Alert>
            )}

            {mapNodes(accessInformation.credentials, (credentials) => (
                <CredentialsCard
                    key={credentials.id}
                    credentials={credentials}
                    reportId={reportId}
                    versionId={versionId}
                />
            ))}
        </>
    );
};

type FilesSectionProps = {
    reportId: number;
    versionId: number;
};

export const FilesSection: FC<FilesSectionProps> = ({ reportId, versionId }) => {
    const { data, loading, error } = useReportVersionAccessInformationFilesQuery({
        notifyOnNetworkStatusChange: true,
        variables: { versionId: reportVersionGraphId(versionId) },
    });
    const accessInformation = data?.reportVersion?.accessInformation;
    const { canCreateFile } = useReportAccessInformationPermissions(accessInformation?.permissions);

    if (loading && !accessInformation) {
        return <LoadingSpinner />;
    }
    if (error) {
        return <Alert variant="danger">{error.message}</Alert>;
    }
    if (!accessInformation) {
        return <Alert variant="danger">Not found.</Alert>;
    }

    return (
        <>
            <Toolbar>
                <ToolbarTitle>
                    <h2>Files</h2>
                </ToolbarTitle>
                <ToolbarButtons>
                    {canCreateFile ? (
                        <ButtonLink
                            to={createReportVersionAccessInformationFileRoute.build({
                                reportId,
                                versionId,
                            })}
                            variant="success"
                        >
                            <FaPlus /> New file
                        </ButtonLink>
                    ) : null}
                </ToolbarButtons>
            </Toolbar>

            {accessInformation.files?.edges?.length === 0 && (
                <Alert variant="warning">No files have been provided.</Alert>
            )}

            {mapNodes(accessInformation.files, (file) => (
                <FileCard key={file.id} file={file} reportId={reportId} versionId={versionId} />
            ))}
        </>
    );
};

export const ReportVersionAccessInformationScreen: FC = () => {
    const { reportId, versionId } = useRouteParams(reportVersionAccessInformationRoute);
    const { data, loading, error } = useReportVersionAccessInformationQuery({
        notifyOnNetworkStatusChange: true,
        variables: { versionId: reportVersionGraphId(versionId) },
    });
    const accessInformation = data?.reportVersion?.accessInformation;
    const { canUpdate } = useReportAccessInformationPermissions(accessInformation?.permissions);

    if (loading && !accessInformation) {
        return <LoadingScreen />;
    }
    if (error) {
        return <ErrorScreen title="Error" message={error.message} />;
    }
    if (!accessInformation) {
        return <ErrorScreen title="Error" message="Not found." />;
    }

    return (
        <NavigationScreen>
            <Container>
                <RouteBreadcrumbs route={reportVersionAccessInformationRoute} />

                <Toolbar>
                    <ToolbarTitle>
                        <h1>Access information</h1>
                    </ToolbarTitle>
                    <ToolbarButtons>
                        {canUpdate ? (
                            <ButtonGroup>
                                <ButtonLink
                                    to={editReportVersionAccessInformationRoute.build({ reportId, versionId })}
                                    variant="warning"
                                >
                                    <FaEdit /> Edit
                                </ButtonLink>
                            </ButtonGroup>
                        ) : null}
                    </ToolbarButtons>
                </Toolbar>

                <h2>Additional information</h2>
                <HtmlContent html={accessInformation.additionalInformation ?? "-"} />

                <div>
                    <CredentialsSection reportId={reportId} versionId={versionId} />
                </div>

                <div>
                    <FilesSection reportId={reportId} versionId={versionId} />
                </div>
            </Container>
        </NavigationScreen>
    );
};
